import api from "@/api";
import { defineStore } from "pinia";
import { School } from "@/interfaces/school.interface";
import {
  InitialSchoolStructure,
  SchoolStructure,
} from "@/interfaces/school-structure.interface";
import { Locale } from "@/interfaces/locale.interface";
import { get } from "lodash";
import { Admin } from "@/interfaces/admin.interface";
import { useFilterStore } from "./filterStore";
import { useDataStore } from "./dataStore";
import moment from "moment";
import i18n from "@/plugins/i18n";

export const useSchoolStore = defineStore({
  id: "schoolStore",
  state: () => ({
    loading: false,
    updateLoader: false,
    localeLoader: false,
    schoolCreationLoader: false,
    schoolStructureLoader: false,
    currentSchool: null as School | null,
    locales: [] as Locale[],
    currentSchoolStructure: null as SchoolStructure | null,
    initialSchoolStructure: {
      id: "",
      teachers: [],
      classGroups: [],
    } as InitialSchoolStructure | null,
    step: 0,
    SelectedGrades: [],
    enableSentModal: false,
  }),
  getters: {
    getLocales(): Locale[] {
      return this.locales;
    },
    getDefaultLocale(): Locale | undefined {
      return this.locales.find((locale) => locale.isDefault);
    },
    getSchoolStructure(): SchoolStructure | null {
      return this.currentSchoolStructure;
    },
    getCurrentSchool(): School | null {
      return this.currentSchool;
    },
    getInitialSchoolStructure(): InitialSchoolStructure | null {
      return this.initialSchoolStructure;
    },
    isLoading(): boolean {
      return this.loading;
    }
  },
  actions: {
    async createSchool(SchoolData: any) {
      try {
        this.schoolCreationLoader = true;

        const response = await api.post(
          `v2/organisation/school/new`,
          SchoolData
        );

        if (response.data.success) {
          //this.currentSchool = response.data.data;
          return response.data.data;
        } else {
          console.error(
            "Failed to add teacher to school:",
            response.data.message
          );
          return false;
        }
      } catch (error) {
        console.error("Error adding teacher to school:", error);
        return false;
      } finally {
        this.schoolCreationLoader = false;
      }
    },
    async fetchLocales() {
      this.localeLoader = true;
      this.locales = [];

      try {
        const response = await api.get("/v1/locales");

        if (response.status === 200) {
          this.locales = response.data;
        } else {
          throw new Error("Failed to fetch locales.");
        }
      } catch (error) {
        console.error("Error fetching locales: ", error);
        this.locales = [];
      } finally {
        this.localeLoader = false;
      }
    },
    async updateSchool(schoolId: string, schoolDetails: Partial<School>) {
      try {
        this.updateLoader = true;

        // Flatten the payload by spreading location properties into the main payload
        const { location, ...otherData } = schoolDetails;
        const flattenedPayload = {
          ...otherData,
          // Flatten city and country from location into the main payload
          ...location,
        };

        const response = await api.put(
          `/v2/organisation/school/update/${schoolId}`,
          flattenedPayload
        );

        if (response.data.success) {
          this.currentSchool = response.data.data;
          return true;
        } else {
          console.error("Failed to update school:", response.data.message);
          return false;
        }
      } catch (error) {
        console.error("Error updating school:", error);
        return false;
      } finally {
        this.updateLoader = false;
      }
    },
    async validateEmail(email: string) {
      try {
        const emailValidationResponse = await api.get(
          `/v2/access/admin/validate-email?email=${encodeURIComponent(email)}`
        );

        if (emailValidationResponse.status !== 200) {
          throw new Error("Failed to validate email from API.");
        }

        const emailValidationData = emailValidationResponse.data.exists;
        return emailValidationData;
      } catch (error: any) {
        console.log("Error: ", error);
        // if (error.code) {
        //   throw new Error(error.message);
        // }
      }
    },
    updateSchoolChampion(champion: Admin) {
      this.currentSchoolStructure?.schoolChampions.push(champion);
    },
    updateEnableSentModal(value: boolean) {
      this.enableSentModal = value;
    },
    updateClassGroups(classGroups: any) {
      if (this.initialSchoolStructure) {
        this.initialSchoolStructure.classGroups = classGroups;
      }
    },
    updateGrades(grades: any) {
      this.SelectedGrades = grades;
    },
    updateCurrentStep(step: number) {
      this.step = step;
    },
    updateTeachers(data: any) {
      if (this.initialSchoolStructure) {
        this.initialSchoolStructure.teachers = data;
      }
    },
    async fetchSchool(schoolId: string) {
      this.loading = true;
      try {
        const schoolResponse = await api.get(
          `v2/organisation/school/single/${schoolId}`
        );

        if (schoolResponse.status !== 200) {
          throw new Error("Failed to fetch school data from API.");
        }

        const school = schoolResponse.data.data;
        this.currentSchool = school;

        this.setLocale();
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchSchoolStructure(schoolId: string) {
      this.schoolStructureLoader = true;
      try {
        const schoolStructureResponse = await api.get(
          `v2/organisation/school/structure/${schoolId}`
        );

        if (schoolStructureResponse.status !== 200) {
          throw new Error("Failed to fetch school data from API.");
        }

        const schoolStructure = schoolStructureResponse.data.data;
        this.currentSchoolStructure = schoolStructure;
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.schoolStructureLoader = false;
      }
    },
    async updateSchoolDetails(
      schoolId: string,
      schoolDetails: Partial<School>
    ) {
      try {
        this.loading = true;

        console.log('updateSchoolDetails', schoolDetails);

        const response = await api.put(
          `/v2/organisation/school/update-structure/${schoolId}`,
          schoolDetails
        );

        if (response.data.success) {
          this.currentSchoolStructure = response.data.data;

          const filterStore = useFilterStore();
          await filterStore.fetchUpdatedFilters();
        } else {
          console.error("Failed to update school:", response.data.message);
        }
      } catch (error) {
        console.error("Error updating school:", error);
      } finally {
        this.loading = false;
      }
    },
    async addNewTeacherToSchool(teacherData: any) {
      try {
        this.loading = true;

        const response = await api.post(
          `/v2/access/admin/initialise-new`,
          teacherData
        );

        if (response.data.success) {
          this.currentSchool = response.data.data;

          const filterStore = useFilterStore();

          const schoolGroupId = this.currentSchoolStructure?.schoolGroup;
          const schoolId = this.currentSchoolStructure?._id;
          const subject = filterStore.selectedSubject;
          const grade = filterStore.selectedGrade;
          const classGroupId = filterStore.selectedClassGroups;

          const dataStore = useDataStore();
          dataStore.fetchInitialData();

          if (schoolGroupId) {
            filterStore.selectSchoolGroup(schoolGroupId);
          }

          if (schoolId) {
            filterStore.selectSchool(schoolId);
          }

          if (subject) {
            filterStore.selectSubject(subject._id);
          }
          
          return response.data.data;
        } else {
          console.error(
            "Failed to add teacher to school:",
            response.data.message
          );
          return response.data.message;
        }
      } catch (error) {
        console.error("Error adding teacher to school:", error);
      } finally {
        this.loading = false;
      }
    },
    async updateTeacherDetails(teacherId: string, teacherDetails: any) {
      try {
        this.loading = true;

        const response = await api.put(
          `/v2/organisation/school-admins/${teacherId}`,
          teacherDetails
        );

        if (response.data.success) {
          this.currentSchool = response.data.data;

          const filterStore = useFilterStore();

          const schoolGroupId = this.currentSchoolStructure?.schoolGroup;
          const schoolId = this.currentSchoolStructure?._id;
          const subject = filterStore.selectedSubject;
          const grade = filterStore.selectedGrade;
          const classGroupId = filterStore.selectedClassGroups;

          const dataStore = useDataStore();
          dataStore.fetchInitialData();

          if (schoolGroupId) {
            filterStore.selectSchoolGroup(schoolGroupId);
          }

          if (schoolId) {
            filterStore.selectSchool(schoolId);
          }

          if (subject) {
            filterStore.selectSubject(subject._id);
          }
        } else {
          console.error("Failed to update teacher:", response.data.message);
        }
      } catch (error) {
        console.error("Error updating teacher:", error);
      } finally {
        this.loading = false;
      }
    },
    async initialiseSchoolStructure(
      initialStructureData: InitialSchoolStructure
    ) {
      try {
        this.loading = true;

        const response = await api.post(
          `/v2/organisation/school/initialise-structure`,
          initialStructureData
        );
        
        if (response.data.success) {
          this.currentSchoolStructure = response.data.data;
          //set new filters
          const filterStore = useFilterStore();
          await filterStore.fetchUpdatedFilters();
          return true;
        } else {
          console.error(
            "Failed to initialise school structure:",
            response.data.error || response.data.message
          );
          // throw new Error(
          //   response.data.error || response.data.message || "Unknown error"
          // );
          return false;
        }
      } catch (error: any) {
        console.error("Error initialising school structure:", error);
        return false;
        // throw error;
      } finally {
        this.loading = false;
      }
    },
    async archiveOrUnarchiveSchool(schoolId: string, action: "archive" | "unarchive") {
      this.loading = true;
      try {
        const response = await api.post(`/v2/organisation/school/archiving`, {
          schoolId,
          action,
        });
        if (response.status === 200) {
          return response.data;
        } else {
          console.error(`Failed to ${action} school`);
          return false;
        }
      } catch (error) {
        console.error(`Error while trying to ${action} school:`, error);
        return false;
      } finally {
        this.loading = false;
      }
    },
    async updateAdminRoles(adminIds: string[], schoolId: string) {
      this.loading = true;
      try {
        const response = await api.patch('/v2/organisation/school-admins/update-roles', {
          adminIds,
          schoolId,
        });
        if (response.status === 200) {
          return response.data;
        } else {
          console.error('Failed to update admin roles');
          return false;
        }
      } catch (error: any) {
        console.error('Error updating admin roles:', error);
        return false;
      } finally {
        this.loading = false;
      }
    },
    setLocale() {
      const preferredLang = get(this.currentSchool, "preferredLang", "en-ZA");
      
      i18n.global.locale.value = preferredLang as "en-ZA" | "en-GB";
      moment.locale(preferredLang);
    }
  },
  persist: true,
});
